import { del, get, post, put } from '@/services/http'
import { endpoints } from '@/services/endpoints'

/**
 * Roles and the permission catalogue.
 *
 * ── Two different kinds of thing ─────────────────────────────────────────
 * A PERMISSION is a capability the server checks for — `assets.manage_assets`,
 * `work_orders.manage`. The catalogue is seeded, read-only, and the same for
 * every account: ten modules holding seventeen permissions between them. Nobody
 * mints one from the UI, because a permission nothing in the code checks for
 * would grant exactly nothing.
 *
 * A ROLE is a named bundle of them, and that IS editable — an account's own way
 * of saying what "Maintenance Technician" means here.
 *
 * ── `target_level` is carried and not yet used ───────────────────────────
 * Every permission names the levels it can be granted at: `account.manage` is
 * account-only, `assets.manage_assets` can be granted at any of the four
 * (account, plant, line, machine). That is
 * what makes this scoped RBAC rather than flat — "manage assets, but only at
 * Findlay". The scope is stored per USER against the grant, not on the role, so
 * a role carries permissions and the scope is chosen when somebody is given it.
 * Kept on the record here so the editor can say where a permission will apply
 * once that screen exists.
 */

/** Wire → app, for one permission. */
const toPermission = (row) => ({
  id: row.id,
  key: row.key,
  name: row.name,
  description: row.description,
  /* The levels this permission may be granted at, outermost first. Read-only
     here; see the header. */
  targetLevels: row.target_level ?? [],
})

const toModule = (row) => ({
  id: row.id,
  key: row.key,
  name: row.name,
  description: row.description,
  permissions: (row.permissions ?? []).map(toPermission),
})

/**
 * The whole catalogue, in one read.
 *
 * Not paged, and it does not need to be: it is seeded reference data of a fixed
 * size. It also never changes without a deploy, which is why the query that
 * wraps this holds it for the session.
 */
export async function fetchPermissions(signal) {
  const data = await get(endpoints.permissions.list, { signal })
  return { modules: (data?.modules ?? []).map(toModule) }
}

const toRole = (row) => ({
  id: row.id,
  name: row.name,
  description: row.description,
  /* `account_id IS NULL` on the server. A system role is visible to every
     account and refuses both update and delete — so this is what decides
     whether a row is editable at all. */
  isSystem: row.is_system === true,
  /* The plant that owns the role, or null for one that applies across the
     account. Create-only: `RoleIn` has no `plant_id`, so it never moves. */
  plantId: row.plant_id ?? null,
  permissionIds: row.permission_ids ?? [],
})

/**
 * Whether a permission can sit on a plant's own role. Such a role is only ever
 * assigned inside its plant, so the server refuses a permission that attaches
 * at account level alone (`_verify_plant_role_permissions`).
 */
export const fitsPlantRole = (permission) =>
  permission.targetLevels.some((level) => level !== 'account')

/**
 * The account's roles, system ones included.
 *
 * `page_size` is asked at the ceiling for the same reason the asset-class
 * library is: the page counts system against custom, and a count off one page
 * is not a count. An account with more than 200 roles is not a thing.
 */
export async function fetchRoles(signal) {
  const data = await get(endpoints.roles.list, {
    params: { page: 1, page_size: 200, sort_by: 'name', sort_dir: 'asc' },
    signal,
  })

  return {
    items: (data?.roles ?? []).map(toRole),
    total: data?.total ?? 0,
    /* The server counts these across the whole set rather than the page, so they
       are right even if the list above is not. */
    counts: {
      system: data?.counts?.is_system?.true ?? 0,
      custom: data?.counts?.is_system?.false ?? 0,
    },
  }
}

/**
 * Create a role.
 *
 * `RoleCreate` is four fields: a name, a description, the permission ids and
 * `plant_id` (create-only). Both strings are required with a minimum length of one — a description is
 * not optional here, which is worth knowing because it usually is.
 *
 * The server refuses a name already taken by any visible role, system ones
 * included, and refuses an unknown permission id. Both come back as field-keyed
 * reasons, so the form can put them where they belong.
 *
 * @param {{name: string, description: string, permissionIds: string[], plantId: ?string}} input
 */
export async function createRole({ name, description, permissionIds, plantId }) {
  const row = await post(endpoints.roles.list, {
    name: name.trim(),
    description: description.trim(),
    permission_ids: permissionIds,
    /* Required key, nullable value: null makes an account-wide role, which
       needs an account-level `users.manage` grant; a plant id makes that
       plant's own role, which the grant must cover. */
    plant_id: plantId ?? null,
  })
  return toRole(row)
}

/**
 * Update one.
 *
 * Same three fields as create, and three refusals worth knowing before the
 * button is pressed rather than after:
 *
 *   `role_id`        — a system role cannot be edited at all. Known in advance
 *                      from `isSystem`, so the UI never offers it.
 *   `permission_ids` — a role somebody already HOLDS cannot have its permissions
 *                      changed. The name and description still can. This one is
 *                      not knowable in advance: the roles response carries no
 *                      holder count, so the only way to find out is to ask.
 *   `name`           — already taken by another visible role.
 *
 * All three come back as field-keyed reasons, so the form puts each where it
 * belongs instead of showing one banner for three different problems.
 *
 * @param {{id: string, name: string, description: string, permissionIds: string[]}} input
 */
export async function updateRole({ id, name, description, permissionIds }) {
  const row = await put(endpoints.roles.detail(id), {
    name: name.trim(),
    description: description.trim(),
    permission_ids: permissionIds,
  })
  return toRole(row)
}

/**
 * Delete one.
 *
 * Refused for a system role, and refused outright while anybody holds it — no
 * cascade, no orphaning, which is the right call. The second is reported as
 * `role_id` and, like the permission rule above, cannot be predicted from the
 * list.
 */
export async function deleteRole(id) {
  await del(endpoints.roles.detail(id))
  return { id, deleted: true }
}
